import React from 'react';
import Calculator from '../components/Calculator';
import FAQ from '../components/FAQ';
import SEO from '../components/shared/SEO';

const InvestmentCalculator: React.FC = () => {
  const investmentFaqs = [
    {
      question: "What is an investment calculator for?",
      answer: "An investment calculator helps you estimate how your money might grow over time. It takes into account factors like initial investment, regular contributions, expected rate of return, and investment timeline to project potential future value of your investments."
    },
    {
      question: "How much money am I really going to need to retire?",
      answer: "A common rule of thumb is to aim for 10-12 times your annual salary by retirement age. However, the actual amount depends on factors like your desired lifestyle, expected retirement age, and anticipated expenses. Many financial advisors recommend saving 15% of your gross income for retirement."
    },
    {
      question: "How do I get started with investing?",
      answer: "Start by: 1) Building an emergency fund, 2) Understanding your investment goals and risk tolerance, 3) Researching different investment options, 4) Opening an investment account (like a 401(k) or IRA), and 5) Creating a diversified portfolio aligned with your goals."
    }
  ];

  return (
    <>
      <SEO 
        title="Investment Calculator"
        description="Calculate investment returns, compound interest, and portfolio growth with our free calculator. Plan smarter with interactive charts and expert insights."
        keywords={[
          'investment calculator',
          'compound interest',
          'investment growth',
          'financial planning',
          'wealth calculator',
          'investment returns',
          'savings growth',
          'investment projection',
          'portfolio growth',
          'compound interest calculator'
        ]}
      />
      <Calculator />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden p-6 sm:p-8 space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Essential Investment Terms</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-800">Expected Return Rate</h3>
                <p className="text-gray-600">The anticipated annual percentage increase in your investment's value. Historical stock market returns have averaged around 7-10% annually over the long term, though past performance doesn't guarantee future results.</p>
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-800">Investment Timeline</h3>
                <p className="text-gray-600">The length of time you plan to keep your money invested. Longer investment periods typically allow for more growth through compound interest and can help smooth out market volatility.</p>
              </div>
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-gray-800">Compound Interest</h3>
                <p className="text-gray-600">The process where your investment earnings generate additional earnings over time. This powerful concept helps explain why starting to invest early can lead to significantly larger returns.</p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Types of Investments</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Stocks</h3>
                  <p className="text-gray-600">Ownership shares in companies that can provide capital appreciation and dividends. Generally higher risk but potential for higher returns.</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Bonds</h3>
                  <p className="text-gray-600">Loans to governments or corporations that pay regular interest. Typically lower risk but offer lower potential returns than stocks.</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">ETFs</h3>
                  <p className="text-gray-600">Exchange-Traded Funds that track indexes, sectors, or commodities. Offer diversification and typically have lower fees than mutual funds.</p>
                </div>
              </div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Mutual Funds</h3>
                  <p className="text-gray-600">Professionally managed investment pools that can include stocks, bonds, and other securities. Good for diversification but may have higher fees.</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Real Estate</h3>
                  <p className="text-gray-600">Property investments that can provide rental income and appreciation. Can offer good returns but requires more capital and active management.</p>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">CDs</h3>
                  <p className="text-gray-600">Certificates of Deposit from banks that offer guaranteed returns. Very low risk but also lower returns compared to other investments.</p>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Investment Strategies</h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Dollar-Cost Averaging</h3>
                <p className="text-gray-600">Investing a fixed amount regularly, regardless of market conditions. This strategy can help reduce the impact of market volatility and emotion-driven decisions.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Diversification</h3>
                <p className="text-gray-600">Spreading investments across different assets to reduce risk. The old saying "don't put all your eggs in one basket" applies perfectly to investment strategy.</p>
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-800">Long-Term Investing</h3>
                <p className="text-gray-600">Focusing on long-term growth rather than short-term market movements. Historical data shows that longer investment periods tend to smooth out market volatility.</p>
              </div>
            </div>
          </section>

          <FAQ faqs={investmentFaqs} />
        </div>
      </div>
    </>
  );
};

export default InvestmentCalculator;